import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import InputField from "../../../../components/InputFields/InputField";
import { Button } from "../../../../components/Buttons";
import { GREY, BRAND, BASE, BORDER } from "../../../../utils/constants/colors";
import { fontWeight } from "../../../../utils/constants/UI";
import CustomDatePicker from "../../../../components/DatePicker/CustomDatePicker";
import TimeSelector from "../../../../components/TimeSelector/TimeSelector";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import TextArea from "../../../../components/InputFields/TextArea";
import dayjs from "dayjs";
import {
  OUTCOME_LIST,
  options as DURATION_OPTIONS,
} from "../../../../utils/constants";

const useStyles = makeStyles({
  container: {
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "0px",
    borderLeft: `1px solid ${BORDER.DEFAULT}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${GREY.W_100}`,
  },
  formContent: {
    flex: 1,
    overflowY: "auto",
    paddingRight: "4px",
    "&::-webkit-scrollbar": {
      width: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: GREY.W_100,
      borderRadius: "10px",
    },
  },
  field: {
    marginBottom: "8px",
  },
  label: {
    marginBottom: "2px",
    display: "flex",
    alignItems: "center",
  },
  footer: {
    marginTop: "auto",
    paddingTop: "12px",
    borderTop: `1px solid ${GREY.W_100}`,
  },
  halfField: {
    display: "flex",
    gap: "8px",
  },
});

const CALL_OUTCOMES = [
  { id: "Connected", label: "Connected" },
  { id: "Busy", label: "Busy" },
  { id: "Left Voicemail", label: "Left Voicemail" },
  { id: "Wrong Number", label: "Wrong Number" },
  { id: "No Answer", label: "No Answer" },
];

const LINKEDIN_ACTIVITIES = [
  { id: "Connection Request", label: "Connection Request" },
  { id: "Direct Message", label: "Direct Message" },
  { id: "InMail", label: "InMail" },
  { id: "Comment/Engagement", label: "Comment/Engagement" },
];

const QuickLog = ({ activeTab, onSave, isLoading, editData, onCancel }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    date: dayjs(),
    time: "09:00",
    duration: [DURATION_OPTIONS[1].label],
    durationId: [DURATION_OPTIONS[1].id],
    status: [OUTCOME_LIST[0].label],
    statusId: [OUTCOME_LIST[0].id],
    outcome: [CALL_OUTCOMES[0].label],
    outcomeId: [CALL_OUTCOMES[0].id],
    linkedinType: [LINKEDIN_ACTIVITIES[0].label],
    linkedinTypeId: [LINKEDIN_ACTIVITIES[0].id],
    count: "",
    description: "",
    prospectName: "",
    prospectEmail: "",
  });

  useEffect(() => {
    if (editData) {
      const durItem = DURATION_OPTIONS.find(
        (i) => i.label === editData.duration,
      );
      const statusItem = OUTCOME_LIST.find((i) => i.label === editData.status);
      const outcomeItem = CALL_OUTCOMES.find(
        (i) => i.label === editData.outcome,
      );
      const liItem = LINKEDIN_ACTIVITIES.find(
        (i) => i.label === editData.activityType,
      );

      setFormData({
        date: dayjs(editData.date),
        time: editData.time || "09:00",
        duration: editData.duration
          ? [editData.duration]
          : [DURATION_OPTIONS[1].label],
        durationId: durItem ? [durItem.id] : [DURATION_OPTIONS[1].id],
        status: editData.status ? [editData.status] : [OUTCOME_LIST[0].label],
        statusId: statusItem ? [statusItem.id] : [OUTCOME_LIST[0].id],
        outcome: editData.outcome
          ? [editData.outcome]
          : [CALL_OUTCOMES[0].label],
        outcomeId: outcomeItem ? [outcomeItem.id] : [CALL_OUTCOMES[0].id],
        linkedinType: editData.activityType
          ? [editData.activityType]
          : [LINKEDIN_ACTIVITIES[0].label],
        linkedinTypeId: liItem ? [liItem.id] : [LINKEDIN_ACTIVITIES[0].id],
        count: editData.count || "",
        description: editData.description || "",
        prospectName: editData.prospect_name || "",
        prospectEmail: editData.prospect_email || "",
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        date: dayjs(),
        count: "",
        description: "",
        prospectName: "",
        prospectEmail: "",
      }));
    }
  }, [editData, activeTab]);

  const handleSave = async () => {
    const common = {
      date: formData.date.format("YYYY-MM-DD"),
      description: formData.description,
    };

    if (editData?._id) {
      common.id = editData._id;
    }

    let specific = {};
    switch (activeTab) {
      case "meetings":
        specific = {
          time: formData.time,
          duration: formData.duration[0],
          status: formData.status[0],
          prospect_name: formData.prospectName,
          prospect_email: formData.prospectEmail,
        };
        break;
      case "calls":
        specific = {
          time: formData.time,
          duration: formData.duration[0],
          outcome: formData.outcome[0],
          count: 1,
        };
        break;
      case "linkedin":
        specific = {
          activityType: formData.linkedinType[0],
          count: 1,
        };
        break;
      default:
        specific = {
          count: formData.count || 1,
        };
    }

    const success = await onSave({ ...common, ...specific });
    if (success && !editData) {
      setFormData((prev) => ({
        ...prev,
        count: "",
        description: "",
        prospectName: "",
        prospectEmail: "",
      }));
    }
  };

  const getTabLabel = () => {
    const labels = {
      calls: "Calls",
      emails: "Emails",
      responses: "Responses",
      linkedin: "LinkedIn",
      meetings: "Meetings",
    };
    return labels[activeTab] || "";
  };

  if (activeTab === "overview") return null;

  const Label = ({ text }) => (
    <Box className={classes.label}>
      <TypographyInline
        size="xs"
        fontWeight={fontWeight.MEDIUM}
        color={GREY.SECONDARY}
        label={text}
      />
    </Box>
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <TypographyInline
          size="md"
          fontWeight={fontWeight.SEMIBOLD}
          color={GREY.PRIMARY}
          label={
            editData
              ? `Edit ${getTabLabel()} of date ${editData.date}`
              : `Log ${getTabLabel()}`
          }
        />
      </Box>

      <Box className={classes.formContent}>
        <Box className={classes.field}>
          <Label text="Log Date" />
          <CustomDatePicker
            size="md40"
            value={formData.date}
            onChange={(date) => setFormData((prev) => ({ ...prev, date }))}
          />
        </Box>

        {(activeTab === "meetings" || activeTab === "calls") && (
          <Box
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {activeTab === "meetings" && (
              <>
                <Box className={classes.field}>
                  <Label text="Prospect Name" />
                  <InputField
                    size="md40"
                    placeholder="John Doe"
                    value={formData.prospectName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        prospectName: e.target.value,
                      }))
                    }
                    type="text"
                  />
                </Box>
                <Box className={classes.field}>
                  <Label text="Prospect Email" />
                  <InputField
                    size="md40"
                    placeholder="john@example.com"
                    value={formData.prospectEmail}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        prospectEmail: e.target.value,
                      }))
                    }
                    type="email"
                  />
                </Box>
              </>
            )}
            <Box className={classes.field}>
              <Label text="Log Time" />
              <TimeSelector
                time={formData.time}
                setTime={(time) => setFormData((prev) => ({ ...prev, time }))}
              />
            </Box>
            <Box className={classes.field}>
              <Label text="Duration" />
              <AutoCompleteCustom
                size="sm36"
                items={DURATION_OPTIONS}
                valueState={formData.duration}
                setValueState={(val) =>
                  setFormData((prev) => ({ ...prev, duration: val }))
                }
                idState={formData.durationId}
                setIdState={(id) =>
                  setFormData((prev) => ({ ...prev, durationId: id }))
                }
                placeholder="Select"
              />
            </Box>
            <Box className={classes.field}>
              <Label
                text={
                  activeTab === "meetings" ? "Meeting Status" : "Call Outcome"
                }
              />
              <AutoCompleteCustom
                size="sm36"
                items={activeTab === "meetings" ? OUTCOME_LIST : CALL_OUTCOMES}
                valueState={
                  activeTab === "meetings" ? formData.status : formData.outcome
                }
                setValueState={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    [activeTab === "meetings" ? "status" : "outcome"]: val,
                  }))
                }
                idState={
                  activeTab === "meetings"
                    ? formData.statusId
                    : formData.outcomeId
                }
                setIdState={(id) =>
                  setFormData((prev) => ({
                    ...prev,
                    [activeTab === "meetings" ? "statusId" : "outcomeId"]: id,
                  }))
                }
                placeholder="Select"
              />
            </Box>
          </Box>
        )}

        {activeTab === "linkedin" && (
          <Box className={classes.field}>
            <Label text="Activity Type" />
            <AutoCompleteCustom
              size="sm36"
              items={LINKEDIN_ACTIVITIES}
              valueState={formData.linkedinType}
              setValueState={(val) =>
                setFormData((prev) => ({ ...prev, linkedinType: val }))
              }
              idState={formData.linkedinTypeId}
              setIdState={(id) =>
                setFormData((prev) => ({ ...prev, linkedinTypeId: id }))
              }
              placeholder="Select"
            />
          </Box>
        )}

        {(activeTab === "emails" || activeTab === "responses") && (
          <Box className={classes.field}>
            <Label
              text={
                activeTab === "emails"
                  ? "Number of Emails Sent"
                  : "Number of Responses Received"
              }
            />
            <InputField
              type="number"
              size="md40"
              placeholder={activeTab === "emails" ? "e.g. 100" : "e.g. 25"}
              value={formData.count}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, count: e.target.value }))
              }
            />
          </Box>
        )}

        <Box className={classes.field}>
          <Label text="Description / Notes" />
          <TextArea
            placeholder={
              activeTab === "meetings"
                ? "Meeting goals or next steps..."
                : activeTab === "calls"
                  ? "Call summary or follow-up tasks..."
                  : "Add some context here..."
            }
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
            minRows={
              activeTab === "linkedin" ||
              activeTab === "emails" ||
              activeTab === "responses"
                ? 10
                : 3
            }
          />
        </Box>
      </Box>

      <Box className={classes.footer} style={{ display: "flex", gap: "10px" }}>
        {editData && (
          <Button
            label="Cancel"
            variant="secondary"
            size="md40"
            fullWidth
            onClick={onCancel}
          />
        )}
        <Button
          label={editData ? "Update Log" : "Submit Log"}
          variant="primary"
          size="md40"
          fullWidth
          onClick={handleSave}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default QuickLog;
